"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GetScoresResponseV3 = exports.GetScoresQueryV3 = exports.SCORE_FIELD_GROUPS_V3 = void 0;
const zod_1 = require("zod");
const scores_1 = require("../../../../../domain/scores");
const zod_2 = require("../../../../../utils/zod");
const schemas_1 = require("./schemas");
exports.SCORE_FIELD_GROUPS_V3 = [
    "core",
    "details",
    "subject",
    "annotation",
];
const fieldsParam = (0, zod_2.commaSeparatedEnumArray)(exports.SCORE_FIELD_GROUPS_V3, ["core"]);
// Accepts case-insensitive input for uppercase enum allowedValues (e.g. "api"
// or "API" both parse to "API"). Downstream code only sees the canonical form.
const csvEnumParam = (allowedValues, label) => zod_1.z
    .string()
    .transform((val) => val
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .map((v) => v.toUpperCase()))
    .pipe(zod_1.z.array(zod_1.z.string()).superRefine((values, ctx) => {
    for (const v of values) {
        if (!allowedValues.includes(v)) {
            ctx.addIssue({
                code: "custom",
                message: `Invalid ${label} value: "${v}". Allowed: ${allowedValues.join(", ")}`,
            });
        }
    }
}))
    .optional();
// GET /v3/scores — all filter params optional; unknown query params return 400 via .strict();
// cross-field superRefine validation lives in the handler.
exports.GetScoresQueryV3 = zod_1.z
    .object({
    limit: zod_2.publicApiPaginationLimitZod,
    fields: fieldsParam,
    // Identifier filters (multi-value, comma-separated)
    id: zod_2.optionalCommaSeparatedStringArray,
    name: zod_2.optionalCommaSeparatedStringArray,
    source: csvEnumParam(scores_1.ScoreSourceArray, "source"),
    dataType: csvEnumParam(scores_1.ScoreDataTypeArray, "dataType"),
    environment: zod_2.optionalCommaSeparatedStringArray,
    configId: zod_2.optionalCommaSeparatedStringArray,
    queueId: zod_2.optionalCommaSeparatedStringArray,
    authorUserId: zod_2.optionalCommaSeparatedStringArray,
    // Value filters
    value: zod_2.optionalCommaSeparatedStringArray,
    // Treat empty string as absent so `?valueMin=` doesn't silently coerce to 0
    // and narrow results to `value >= 0`. Zod 4 rejects ±Infinity / NaN out of
    // the box, so `.finite()` is not needed.
    valueMin: zod_1.z.preprocess((v) => (v === "" ? undefined : v), zod_1.z.coerce.number().optional()),
    valueMax: zod_1.z.preprocess((v) => (v === "" ? undefined : v), zod_1.z.coerce.number().optional()),
    // Entity-bounded filters
    traceId: zod_2.optionalCommaSeparatedStringArray,
    sessionId: zod_2.optionalCommaSeparatedStringArray,
    observationId: zod_2.optionalCommaSeparatedStringArray,
    experimentId: zod_2.optionalCommaSeparatedStringArray,
    // Timestamp filters — preprocess empty string to undefined so ?fromTimestamp=
    // from a templating system is treated as absent, consistent with valueMin/valueMax.
    fromTimestamp: zod_1.z.preprocess((v) => (v === "" ? undefined : v), zod_1.z.coerce.date().optional()),
    toTimestamp: zod_1.z.preprocess((v) => (v === "" ? undefined : v), zod_1.z.coerce.date().optional()),
    // Deferred params (always → 400 — require trace JOIN not present in v3).
    // Treat the empty string as absent so a stray `?userId=` from a templating
    // system doesn't trigger the "use v2" 400 spuriously.
    userId: zod_1.z
        .string()
        .optional()
        .transform((v) => (v === "" ? undefined : v)),
    traceTags: zod_1.z
        .string()
        .optional()
        .transform((v) => (v === "" ? undefined : v)),
})
    .strict();
exports.GetScoresResponseV3 = zod_1.z.object({
    data: zod_1.z.array(schemas_1.APIScoreSchemaV3),
    meta: zod_1.z.object({
        limit: zod_1.z.number(),
        cursor: zod_1.z.string().optional(),
    }),
});
